import * as React from "react";
import {useEffect, useState} from "react";
import {AIAgentTask, AiScriptChangeIteration} from "../../../../../generated/api";
import ReactTable from "react-table";
import {AiAgentTaskIterationModal} from "./AiAgentTaskIterationModal";
import {Button, Label, Modal} from "semantic-ui-react";
import {workingConfig} from "../ConfigSignal";
import AceEditor from "react-ace";
import {aiAgentTasks} from "./AiScripterPage";
import {TaskStatsModal} from "./taskStatsModal";
import {ScriptCherryPickModal} from "../ScriptConverterPlayground/ScriptCherryPickerModal";
import {FaCodeMerge} from "react-icons/fa6";
import {ScriptCompareModal} from "./ScriptCompareModal";
import { ConversionResultsComparisonModal } from "./ConversionResultsComparisonModal";

export interface AiAgentTaskIterationTableProps {
    aiAgentTask: AIAgentTask | null,
}

export function AiAgentTaskIterationTable(props: React.PropsWithChildren<AiAgentTaskIterationTableProps>) {

    const [iterationToOpen, setIterationToOpen] = useState<AiScriptChangeIteration | null>(null);
    const [iterationToCompare, setIterationToCompare] = useState<AiScriptChangeIteration | null>(null);
    const [iterationToPickFrom, setIterationToPickFrom] = useState<AiScriptChangeIteration | null>(null);
    const [iterationIndex, setIterationIndex] = useState<number | null>(null);
    const [sorted, setSorted] = useState([{id: 'timestamp', desc: false}]);
    const [iterations, setIterations] = useState<AiScriptChangeIteration[]>([]);
    const [showLLMInputModal, setShowInputModal] = useState(false);
    const [currentLLMInputPrompt, setCurrentInputPrompt] = useState("");
    const [showLLMOutputModal, setShowOutputModal] = useState(false);
    const [currentLLMOutputResponse, setCurrentOutputResponse] = useState("");
    const [showConversionComparisonModal, setShowConversionComparisonModal] = useState(false);

    useEffect(() => {
        refreshIterations()
    }, [props.aiAgentTask, aiAgentTasks.value]);

    function refreshIterations() {
        let refreshedTask = aiAgentTasks.value.find((task: AIAgentTask) => task._id === props.aiAgentTask?._id)
        if (refreshedTask) {
            setIterations(refreshedTask.iterations);
        } else {
            if (props.aiAgentTask) {
                setIterations(props.aiAgentTask.iterations);
            }
        }
    }
    const handleShowRawOutput = (iteration: AiScriptChangeIteration, e: React.MouseEvent) => {
        e.stopPropagation();
        const rawResponse = iteration.aiModelResponseScripting?.rawAiModelResponse || "No raw response available";
        setCurrentOutputResponse(rawResponse);
        setShowOutputModal(true);
    };

    function iterationTable() {
        let columns = [
            {
                Header: "ID",
                accessor: "_id",
                width: 30,
                Cell: (d: any) => <p>{d.index + 1}</p>
            },
            {
                Header: "Status",
                accessor: "status",
                width: 190,
                Cell: ({original: {status}}) => (
                    <Label color={getStatusColor(status)}>{status}</Label>
                )
            },
            {
                Header: "Type",
                width: 400,
                Cell: ({original: {status, userInputPrompt, aiModelResponseScripting}}) => {
                    if (status === "ABORTED") {
                        return <Label color={"red"}>{userInputPrompt?.toUpperCase() || "ABORTED"}</Label>;
                    }

                    const executeFunc = aiModelResponseScripting?.aiModelResponse?.executeFunc;
                    return executeFunc ? (
                        <Label color={getTypeLabelColor(executeFunc)}>{executeFunc}</Label>
                    ) : null;
                }
            },
            {
                id: "Diff",
                Header: "Diff",
                width: 50,
                Cell: (d: any) => {
                    if (d.original.aiModelResponseScripting?.aiModelResponse?.script == null) {
                        return null;
                    }
                    return <Button
                        icon="exchange"
                        size="tiny"
                        color="blue"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIterationIndex(d.index);
                            setIterationToCompare(d.original);
                        }}
                    />;
                }
            },
            {
                id: "Pick",
                Header: "Pick",
                width: 50,
                Cell: (d: any) => {
                    if (d.original.aiModelResponseScripting?.aiModelResponse?.script == null) {
                        return null;
                    }
                    return <Button
                        icon
                        color="blue"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIterationIndex(d.index);
                            setIterationToPickFrom(d.original);
                        }}
                        style={{padding: "0.5em"}}
                        title="Script aus dieser Iteration übernehmen"
                    >
                        <FaCodeMerge style={{fontSize: "1.2em", verticalAlign: "middle"}}/>
                    </Button>
                }
            },
            {
                id: "Input",
                Header: "Input",
                width: 50,
                Cell: (d: any) => {
                    return d.original.inputPrompt ? (
                        <Button
                            icon="file text"
                            size="tiny"
                            color="teal"
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentInputPrompt(d.original.inputPrompt);
                                setShowInputModal(true);
                            }}
                            title="View raw Input to AI Model"
                        />
                    ) : null;
                }
            },
            {
                id: "rawOutput",
                Header: "Output",
                width: 50,
                // Use a render function that doesn't depend on event handlers in the cell
                Cell: ({original}) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            icon="file code outline"
                            size="tiny"
                            color="violet"
                            onClick={(e) => handleShowRawOutput(original, e)}
                            title="View Raw Output from AI Model"
                        />
                    </div>
                )
            },
            {
                Header: "Created At",
                accessor: "timestamp",
                width: 160,
                Cell: (row) => {
                    return <div>{new Date(row.original.timestamp).toLocaleString()}</div>;
                }
            },
            {
                Header: "Tokens",
                width: 160,
                Cell: (row) => {
                    const usage = row.original.aiModelResponseScripting?.totalUsageTokensNumber || 0;
                    const completion = row.original.aiModelResponseScripting?.totalCompletionTokensNumber || 0;
                    return usage + completion > 0 ? (
                        <div>
                            <div>Usage: {usage}</div>
                            <div>Completion: {completion}</div>
                        </div>
                    ) : null;
                }
            },
            {
                Header: "Price",
                width: 90,
                Cell: (row) => {
                    const price = row.original.aiModelResponseScripting?.totalPrice;
                    return price ? (
                        <div>${price.toFixed(6)}</div>
                    ) : null;
                }
            },
            {
                Header: "Duration",
                width: 100,
                Cell: (row) => {
                    const duration = row.original.durationMs;
                    return duration ? (
                        <div>{(duration / 1000).toFixed(2)}s</div>
                    ) : null;
                }
            },
        ];

        return (
            <ReactTable
                data={iterations}
                pageSize={iterations.length || 0}
                showPagination={false}
                columns={columns}
                sorted={sorted}
                onSortedChange={newSorted => setSorted(newSorted)}
                className="-striped -highlight custom-row-height"
                style={{cursor: "pointer"}}
                getTdProps={(state, rowInfo, column, instance) => {
                    if (!rowInfo) return {};

                    return {
                        onClick: (e, handleOriginal) => {
                            if (column.id !== 'Diff' && column.id !== 'Input' && column.id !== 'rawOutput') {
                                setIterationToOpen(rowInfo.original);
                                setIterationIndex(rowInfo.index);
                            }

                            if (handleOriginal) {
                                handleOriginal();
                            }
                        },
                        style: {
                            background: "#f9f9f9",
                            color: "black"
                        }
                    };
                }}
            />
        );
    }

    function getStatusColor(status: string) {
        switch (status) {
            case "DONE":
                return "green";
            case "USER_INPUT_REQUIRED":
                return "orange";
            case "RUNNING":
                return "blue";
            case "ABORTED":
                return "red";
            default:
                return "grey";
        }
    }

    function getTypeLabelColor(executeFunc: string) {
        switch (executeFunc) {
            case "GET_USER_ADDITIONAL_INFO":
                return "teal";
            case "EXECUTE_SCRIPT_AND_RETURN_LOGS":
                return "black";
            case "REQUEST_USER_TO_MANUALLY_VERIFY_OUTPUT":
                return "brown";
            default:
                return "blue";
        }
    }

    function getCurrentScript() {
        const jp = require('jsonpath');
        return jp.value(
            JSON.parse(workingConfig.value),
            props.aiAgentTask?.inputScriptPath
        );
    }
    let displayModal = false;
    let currentTaskForStats: AIAgentTask | null = null;

    if (iterations && iterations.length > 0) {
        if (props.aiAgentTask?._id) {
            currentTaskForStats = aiAgentTasks.value.find(task => task._id === props.aiAgentTask._id) || props.aiAgentTask;
        } else {
            currentTaskForStats = props.aiAgentTask;
        }

        const lastIteration = iterations[iterations.length - 1];
        if (lastIteration.status === "DONE" || lastIteration.status === "ABORTED") {
            displayModal = true;
        }
    }

    function saveScriptToConfig(script: string) {
        const jp = require('jsonpath');
        const jsonPath = props.aiAgentTask?.inputScriptPath?.replace(".CompanyConfiguration", "") || "";
        let config = JSON.parse(workingConfig.value);
        jp.value(config, jsonPath, script);
        workingConfig.value = JSON.stringify(config);
    }

    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <h3>Iterations ({iterations.length || 0})</h3>
                {iterations.some(iter => iter.queuedEventConversionResponse?.resultFiles?.length > 0) && (
                    <Button
                        icon="exchange"
                        content="Compare Results"
                        color="grey"
                        size="small"
                        onClick={() => setShowConversionComparisonModal(true)}
                    />
                )}
            </div>
            {iterationTable()}

            {displayModal && currentTaskForStats && (
                <TaskStatsModal task={currentTaskForStats}/>
            )}

            {iterationToOpen && currentTaskForStats && (
                <AiAgentTaskIterationModal
                    isOpen={true}
                    onClose={(xReqId: string | null) => {
                        setIterationToOpen(null);
                        setIterationIndex(null);
                    }}
                    task={currentTaskForStats}
                    iteration={iterationToOpen}
                    indexNr={iterationIndex as number}
                />
            )}
            {iterationToOpen && (
                <AiAgentTaskIterationModal
                    isOpen={true}
                    onClose={(xReqId: string | null) => {
                        setIterationToOpen(null);
                        setIterationIndex(null);
                    }}
                    task={props.aiAgentTask}
                    iteration={iterationToOpen}
                    indexNr={iterationIndex}
                />
            )}
            {iterationToCompare && (
                <ScriptCompareModal
                    isOpen={true}
                    onClose={() => {
                        setIterationToCompare(null);
                    }}
                    scriptA={props.aiAgentTask?.inputScript || ""}
                    titleA="initial Script of Agent Task"
                    scriptB={iterationToCompare.functionCallOutput?.script}
                    titleB={"Script from Iteration " + (iterationIndex + 1)}
                />
            )}
            {iterationToPickFrom && (
                <ScriptCherryPickModal
                    isOpen={true}
                    onClose={() => {
                        setIterationToPickFrom(null);
                    }}
                    left={getCurrentScript()}
                    leftTitle="Current Script in Config"
                    right={iterationToPickFrom.functionCallOutput?.script}
                    rightTitle={"Script from Iteration " + (iterationIndex + 1)}
                    onSave={(script: string) => {
                        saveScriptToConfig(script);
                    }}
                />
            )}

            <Modal
                open={showLLMInputModal}
                onClose={() => setShowInputModal(false)}
                size="large"
            >
                <Modal.Header>Input to AI Model</Modal.Header>
                <Modal.Content>
                    <AceEditor
                        mode="json"
                        theme="monokai"
                        name="input-prompt-editor"
                        value={currentLLMInputPrompt}
                        readOnly={true}
                        fontSize={13}
                        width="100%"
                        height="calc(100vh - 450px)"
                        setOptions={{
                            showPrintMargin: false,
                            highlightActiveLine: false,
                        }}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setShowInputModal(false)}>Close</Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={showLLMOutputModal}
                onClose={() => setShowOutputModal(false)}
                size="large"
            >
                <Modal.Header>Raw Output from AI Model</Modal.Header>
                <Modal.Content>
                    <AceEditor
                        mode="json"
                        theme="monokai"
                        name="output-response-editor"
                        value={currentLLMOutputResponse}
                        readOnly={true}
                        fontSize={13}
                        width="100%"
                        height="calc(100vh - 450px)"
                        setOptions={{
                            showPrintMargin: false,
                            highlightActiveLine: false,
                        }}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setShowOutputModal(false)}>Close</Button>
                </Modal.Actions>
            </Modal>
            {showConversionComparisonModal && (
                <ConversionResultsComparisonModal
                    isOpen={showConversionComparisonModal}
                    onClose={() => setShowConversionComparisonModal(false)}
                    aiAgentTask={currentTaskForStats || props.aiAgentTask}
                />
            )}
        </div>
    );
}